import React, { useState, useEffect } from 'react';
import { Button, Spinner, Alert } from 'react-bootstrap';
import { FiFile, FiX, FiArrowRight, FiArrowLeft, FiPlus, FiTrash2, FiMove } from 'react-icons/fi';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import apiClient from '../../apiClient';
import './DocumentSectionSelector.css';

const DocumentSectionSelector = ({ 
  show, 
  onHide, 
  projectId, 
  documentType, 
  onGenerate 
}) => {
  const [loading, setLoading] = useState(true);
  const [availableSections, setAvailableSections] = useState([]);
  const [selectedSections, setSelectedSections] = useState([]);
  const [customSections, setCustomSections] = useState([]);
  const [error, setError] = useState(null);
  const [showAddSection, setShowAddSection] = useState(false);
  const [newSectionTitle, setNewSectionTitle] = useState('');
  const [newSectionDescription, setNewSectionDescription] = useState('');
  const [newSectionDetails, setNewSectionDetails] = useState('');
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    if (show && projectId && documentType) {
      fetchSuggestedSections();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, projectId, documentType]);

  const fetchSuggestedSections = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get(
        `/documents/sections/${documentType}?project_id=${projectId}`
      );
      const sectionsData = response.data.suggested_sections || [];
      
      // Split sections into selected (recommended) and available
      const selected = sectionsData.filter(section => section.isSelected);
      const available = sectionsData.filter(section => !section.isSelected);
      
      setSelectedSections(selected);
      setAvailableSections(available);
    } catch (err) {
      console.error('Error fetching sections:', err);
      setError('Failed to load suggested sections. Using default sections.');
      // Use default sections
      const defaults = getDefaultSections();
      setSelectedSections(defaults);
      setAvailableSections([]);
    } finally {
      setLoading(false);
    }
  };

  const getDefaultSections = () => {
    const sections = documentType === 'change_management_plan' 
      ? [
          {
            id: 'default_1',
            title: '1.0 Executive Summary',
            description: 'Overview of the change initiative, business case, and expected outcomes',
            details: 'Business rationale • Strategic alignment • Key objectives • Expected benefits • Timeline overview',
            priority: 'recommended',
            isSelected: true,
            order: 1
          },
          {
            id: 'default_2',
            title: '2.0 Change Impact Analysis',
            description: 'Detailed analysis of how the change will affect different areas of the organization',
            details: 'Process impacts • Technology changes • People impacts • Cultural shifts • Resource requirements',
            priority: 'recommended',
            isSelected: true,
            order: 2
          },
          {
            id: 'default_3',
            title: '3.0 Stakeholder Engagement Strategy',
            description: 'Comprehensive plan for engaging and managing stakeholders throughout the change',
            details: 'Stakeholder mapping • Engagement approach • Communication matrix • Resistance management • Feedback mechanisms',
            priority: 'recommended',
            isSelected: true,
            order: 3
          }
        ]
      : [
          {
            id: 'default_1',
            title: '1.0 Communication Objectives',
            description: 'Clear objectives for the communication strategy aligned with project goals',
            details: 'Primary objectives • Success metrics • Target outcomes • Behavioral changes • Message alignment',
            priority: 'recommended',
            isSelected: true,
            order: 1
          },
          {
            id: 'default_2',
            title: '2.0 Audience Analysis',
            description: 'Detailed analysis of target audiences and their communication needs',
            details: 'Audience segments • Information needs • Preferred channels • Cultural considerations • Accessibility requirements',
            priority: 'recommended',
            isSelected: true,
            order: 2
          },
          {
            id: 'default_3',
            title: '3.0 Key Messages',
            description: 'Core messages tailored for different stakeholder groups',
            details: 'Primary messages • Supporting points • Call to action • Benefits messaging • FAQ responses',
            priority: 'recommended',
            isSelected: true,
            order: 3
          }
        ];
    return sections;
  };

  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const { source, destination } = result;

    // Moving within the same list
    if (source.droppableId === destination.droppableId) {
      if (source.droppableId === 'available') {
        const items = Array.from(availableSections);
        const [reorderedItem] = items.splice(source.index, 1);
        items.splice(destination.index, 0, reorderedItem);
        setAvailableSections(items);
      } else {
        const items = Array.from(selectedSections);
        const [reorderedItem] = items.splice(source.index, 1);
        items.splice(destination.index, 0, reorderedItem);
        setSelectedSections(items);
      }
    } else {
      // Moving between lists
      if (source.droppableId === 'available' && destination.droppableId === 'selected') {
        const sourceItems = Array.from(availableSections);
        const destItems = Array.from(selectedSections);
        const [movedItem] = sourceItems.splice(source.index, 1);
        destItems.splice(destination.index, 0, movedItem);
        setAvailableSections(sourceItems);
        setSelectedSections(destItems);
      } else {
        const sourceItems = Array.from(selectedSections);
        const destItems = Array.from(availableSections);
        const [movedItem] = sourceItems.splice(source.index, 1);
        destItems.splice(destination.index, 0, movedItem);
        setSelectedSections(sourceItems);
        setAvailableSections(destItems);
      }
    }
  };

  const moveSection = (section, fromList, toList) => {
    if (fromList === 'available') {
      setAvailableSections(prev => prev.filter(s => s.id !== section.id));
      setSelectedSections(prev => [...prev, section]);
    } else {
      setSelectedSections(prev => prev.filter(s => s.id !== section.id));
      setAvailableSections(prev => [...prev, section]);
    }
  };

  const handleAddCustomSection = () => {
    if (newSectionTitle.trim()) {
      const newSection = {
        id: `custom_${Date.now()}`,
        title: newSectionTitle.trim(),
        description: newSectionDescription.trim(),
        details: newSectionDetails.trim(),
        priority: 'custom',
        isSelected: true,
        isCustom: true,
        order: selectedSections.length + customSections.length + 1
      };
      
      setSelectedSections([...selectedSections, newSection]);
      setCustomSections([...customSections, newSection]);
      setNewSectionTitle('');
      setNewSectionDescription('');
      setNewSectionDetails('');
      setShowAddSection(false);
    }
  };

  const handleRemoveCustomSection = (sectionId) => {
    setSelectedSections(prev => prev.filter(s => s.id !== sectionId));
    setCustomSections(prev => prev.filter(s => s.id !== sectionId));
  };

  const handleGenerate = async () => {
    setGenerating(true);
    setError(null);

    if (selectedSections.length === 0) {
      setError('Please select at least one section to include in the document.');
      setGenerating(false);
      return;
    }

    try {
      const customSectionsOnly = selectedSections.filter(s => s.isCustom);
      await onGenerate(selectedSections, customSectionsOnly);
      onHide();
    } catch (err) {
      console.error('Error generating document:', err);
      setError('Failed to generate document. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  if (!show) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && !generating) {
      onHide();
    }
  };

  const renderSection = (section, index, listType) => (
    <Draggable key={section.id} draggableId={section.id} index={index}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          className={`section-card ${section.priority} ${snapshot.isDragging ? 'dragging' : ''}`}
        >
          <div className="drag-handle" {...provided.dragHandleProps}>
            <FiMove />
          </div>
          <div className="section-content">
            <h6 className="section-title">{section.title}</h6>
            <p className="section-description">{section.description}</p>
            {section.details && (
              <small className="section-details">
                <strong>Includes:</strong> {section.details}
              </small>
            )}
          </div>
          <div className="section-actions">
            {section.isCustom && listType === 'selected' && (
              <button
                className="remove-btn"
                onClick={() => handleRemoveCustomSection(section.id)}
                title="Remove custom section"
              >
                <FiTrash2 />
              </button>
            )}
            <button
              className="move-btn"
              onClick={() => moveSection(section, listType, listType === 'available' ? 'selected' : 'available')}
              title={listType === 'available' ? 'Add to document' : 'Remove from document'}
            >
              {listType === 'available' ? <FiArrowRight /> : <FiArrowLeft />}
            </button>
          </div>
        </div>
      )}
    </Draggable>
  );

  return (
    <div className="document-selector-overlay" onClick={handleBackdropClick}>
      <div className="document-selector-modal wide">
        <div className="document-selector-header">
          <div className="header-content">
            <FiFile className="header-icon" />
            <h2>Build Your {documentType === 'change_management_plan' ? 'Change Management Plan' : 'Communication Plan'}</h2>
          </div>
          <button className="modal-close-btn" onClick={onHide} disabled={generating}>
            <FiX />
          </button>
        </div>

        <div className="document-selector-body">
          {loading ? (
            <div className="loading-container">
              <Spinner animation="border" variant="primary" />
              <p className="mt-3">Analyzing your project to suggest relevant sections...</p>
            </div>
          ) : (
            <DragDropContext onDragEnd={handleDragEnd}>
              {error && (
                <Alert variant="warning" dismissible onClose={() => setError(null)} className="mx-3 mt-3">
                  {error}
                </Alert>
              )}
              
              <div className="drag-drop-container">
                <div className="sections-panel available-panel">
                  <div className="panel-header">
                    <h4>Available Sections</h4>
                    <p>Drag sections to the right to include them in your document</p>
                  </div>
                  <Droppable droppableId="available">
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        className={`sections-list ${snapshot.isDraggingOver ? 'drag-over' : ''}`}
                      >
                        {availableSections.length === 0 ? (
                          <div className="empty-state">
                            <p>All sections have been added to your document</p>
                          </div>
                        ) : (
                          availableSections.map((section, index) => 
                            renderSection(section, index, 'available')
                          )
                        )}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </div>

                <div className="drag-arrow">
                  <FiArrowRight />
                </div>

                <div className="sections-panel selected-panel">
                  <div className="panel-header">
                    <h4>Document Sections ({selectedSections.length})</h4>
                    <p>These sections will be included in your generated document</p>
                  </div>
                  <Droppable droppableId="selected">
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        className={`sections-list ${snapshot.isDraggingOver ? 'drag-over' : ''}`}
                      >
                        {selectedSections.length === 0 ? (
                          <div className="empty-state">
                            <p>Drag sections here to build your document</p>
                          </div>
                        ) : (
                          selectedSections.map((section, index) => 
                            renderSection(section, index, 'selected')
                          )
                        )}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                  
                  <div className="add-custom-section">
                    {showAddSection ? (
                      <div className="add-section-form">
                        <h6>Add Custom Section</h6>
                        <input
                          type="text"
                          placeholder="Section Title (e.g., 6.0 Budget Considerations)"
                          value={newSectionTitle}
                          onChange={(e) => setNewSectionTitle(e.target.value)}
                          className="form-control mb-2"
                        />
                        <textarea
                          placeholder="Brief description..."
                          value={newSectionDescription}
                          onChange={(e) => setNewSectionDescription(e.target.value)}
                          className="form-control mb-2"
                          rows={2}
                        />
                        <input
                          type="text"
                          placeholder="Details (e.g., Budget breakdown • Cost-benefit analysis • ROI projections)"
                          value={newSectionDetails}
                          onChange={(e) => setNewSectionDetails(e.target.value)}
                          className="form-control mb-3"
                        />
                        <div className="d-flex gap-2">
                          <Button 
                            variant="primary" 
                            size="sm"
                            onClick={handleAddCustomSection}
                            disabled={!newSectionTitle.trim()}
                          >
                            Add Section
                          </Button>
                          <Button 
                            variant="outline-secondary" 
                            size="sm"
                            onClick={() => {
                              setShowAddSection(false);
                              setNewSectionTitle('');
                              setNewSectionDescription('');
                              setNewSectionDetails('');
                            }}
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <Button
                        variant="outline-primary"
                        onClick={() => setShowAddSection(true)}
                        className="add-section-btn"
                      >
                        <FiPlus className="me-2" />
                        Add Custom Section
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </DragDropContext>
          )}
        </div>
        
        <div className="document-selector-footer">
          <Button variant="outline-secondary" onClick={onHide} disabled={generating}>
            Cancel
          </Button>
          <Button 
            variant="primary"
            onClick={handleGenerate}
            disabled={loading || generating || selectedSections.length === 0}
          >
            {generating ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Generating Document...
              </>
            ) : (
              <>
                Generate Document with {selectedSections.length} Sections
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DocumentSectionSelector;